
/**
 * Categorizes error types in the Plug Chat application.
 */
export enum EWIRE_TALK_ERROR_TYPE {
  // Authentication Errors
  UNAUTHORIZED = 'UNAUTHORIZED',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',

  // Connection Errors
  CONNECTION_FAILED = 'CONNECTION_FAILED',
  SOCKET_DISCONNECTED = 'SOCKET_DISCONNECTED',

  // Room Errors
  ROOM_NOT_FOUND = 'ROOM_NOT_FOUND',
  ROOM_FULL = 'ROOM_FULL',

  // Peer Errors
  PEER_ALREADY_EXISTS = 'PEER_ALREADY_EXISTS',
  PEER_CONNECTION_ERROR = 'PEER_CONNECTION_ERROR',

  // Message Errors
  MESSAGE_SEND_FAILED = 'MESSAGE_SEND_FAILED',
  MESSAGE_TOO_LONG = 'MESSAGE_TOO_LONG',

  // General Errors
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR'
}

/**
 * Custom error class for structured error handling.
 */
export class WireTalkError extends Error {
  /** Specific error classification */
  type: EWIRE_TALK_ERROR_TYPE;

  /** Additional error metadata */
  context?: Record<string, unknown>;

  /**
   * Creates a new error instance.
   * @param message Descriptive error message
   * @param type Error classification
   * @param context Optional error metadata
   */
  constructor(
    message: string,
    type: EWIRE_TALK_ERROR_TYPE = EWIRE_TALK_ERROR_TYPE.UNKNOWN_ERROR,
    context?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'WireTalkError';
    this.type = type;
    this.context = context;

    // Maintain proper stack trace
    Object.setPrototypeOf(this, WireTalkError.prototype);
  }

  /**
   * Converts error to a serializable object.
   * @returns Structured error representation
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      type: this.type,
      context: this.context,
      stack: this.stack
    };
  }

  /**
   * Adds context to the existing error.
   * @param context Additional error metadata
   * @returns New error instance with combined context
   */
  withContext(context: Record<string, unknown>): WireTalkError {
    return new WireTalkError(this.message, this.type, {
      ...this.context,
      ...context
    });
  }

  /**
   * Checks if error matches a specific type.
   * @param type Error type to compare
   * @returns Whether error matches the type
   */
  isType(type: EWIRE_TALK_ERROR_TYPE): boolean {
    return this.type === type;
  }
}

/**
 * Centralized error handling and logging utility.
 */
export class WireTalkErrorHandler {
  /**
   * Logs an error with optional additional context.
   * @param error Error to log
   * @param additionalContext Optional logging metadata
   */
  static log(
    error: Error | WireTalkError,
    additionalContext?: Record<string, unknown>
  ) {
    const wireTalkError = error instanceof WireTalkError ?
      error :
      new WireTalkError(error.message);

    console.error('WireTalk Error:', {
      message: wireTalkError.message,
      type: wireTalkError.type,
      context: {
        ...wireTalkError.context,
        ...additionalContext
      },
      stack: wireTalkError.stack
    });
  }

  /**
   * Creates a new error instance.
   * @param message Error message
   * @param type Error classification
   * @param context Optional error metadata
   * @returns New WireTalkError instance
   */
  static create(
    message: string,
    type: EWIRE_TALK_ERROR_TYPE = EWIRE_TALK_ERROR_TYPE.UNKNOWN_ERROR,
    context?: Record<string, unknown>
  ): WireTalkError {
    return new WireTalkError(message, type, context);
  }
}
